import * as amplitude from "@amplitude/analytics-browser";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { uploadFile } from "@redotech/redo-api-client/conversations";
import PaperclipIcon from "@redotech/redo-icons/paperclip_filled.svg";
import { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { ConversationTagWithId } from "@redotech/redo-model/conversation";
import { UploadedFile } from "@redotech/redo-model/file/uploaded-file";
import {
  Macro,
  MACRO_VARIABLES,
  MacroAutomation,
  MacroStatusToSet,
  MacroVariable as MacroVariableType,
  SnoozeDuration,
} from "@redotech/redo-model/macro";
import { PutMacroBody } from "@redotech/redo-model/put-macro-body";
import { conversationFileUploadErrorMessages } from "@redotech/redo-model/support/conversations/conversation-file-upload-error";
import { Team } from "@redotech/redo-model/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoClientContext } from "@redotech/redo-web/client";
import { Flex } from "@redotech/redo-web/flex";
import ClearFormatIcon from "@redotech/redo-web/icon-old/clear-format.svg";
import {
  clearColoringAtCursor,
  insertMacroVariableIntoQuill,
  MacroVariablePill,
} from "@redotech/redo-web/macro-variables";
import { QuillAttachmentCarousel } from "@redotech/redo-web/quill/quill-attachment-carousel";
import { QuillEditor } from "@redotech/redo-web/quill/quill-editor";
import * as quillEditorCss from "@redotech/redo-web/quill/quill-editor.module.css";
import { QuillToolbarOptions } from "@redotech/redo-web/quill/quill-toolbar-options";
import { QuillToolbarUploadFile } from "@redotech/redo-web/quill/quill-toolbar-upload-file";
import { Text } from "@redotech/redo-web/text";
import {
  groupInput,
  input,
  nonEmptyValidator,
  optionalInput,
} from "@redotech/ui/form";
import { arrayEqual } from "@redotech/util/equal";
import { emailValidator } from "@redotech/web-util/form";
import Quill from "quill";
import {
  ChangeEvent,
  Fragment,
  memo,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ConversationTagInput } from "../conversation-tags/conversation-tag-input";
import { ConversationTagsContext } from "../services/conversation-tags-service";
import { AddInternalNoteAutomation } from "./macro-automations/add-internal-note";
import { ForwardMessageAutomationEditMode } from "./macro-automations/forward-message";

const macroBody = groupInput({
  macroName: input<string>({ validator: nonEmptyValidator }),
  statusToSet: optionalInput(
    input<MacroStatusToSet | undefined>(),
    () => undefined,
  ),
  snoozeDuration: optionalInput(
    input<SnoozeDuration | undefined>(),
    () => undefined,
  ),
  tagsToAdd: input<ConversationTagWithId[]>({
    equal: arrayEqual((a, b) => a.name === b.name),
  }),
  emailSubject: optionalInput(
    input<string | undefined>({ validator: nonEmptyValidator }),
    () => undefined,
  ),
  shouldAddNote: optionalInput(input<boolean | undefined>(), () => undefined),
  noteToAddUsersMentioned: optionalInput(
    input<string[] | undefined>(),
    () => undefined,
  ),
  shouldForwardMessage: optionalInput(
    input<boolean | undefined>(),
    () => undefined,
  ),
  messageToForwardExtraContent: optionalInput(
    input<string | undefined>(),
    () => undefined,
  ),
  messageToForwardEmailAddress: optionalInput(
    input<string>({ validator: emailValidator }),
    () => "",
  ),
});

/**
 * Used to edit existing macros or create new ones
 */
export const MacroEditorModal = memo(function MacroEditorModal({
  client,
  cancel,
  submitMacro,
  initialState,
  team,
}: {
  client: RedoMerchantClient;
  cancel(): void;
  submitMacro(macro: PutMacroBody): Promise<void>;
  initialState: Macro | undefined;
  team: Team;
}) {
  const apiClient = useRequiredContext(RedoClientContext);

  const [conversationTags] = useLazyContext(ConversationTagsContext);

  const initialTagNames = new Set<string>(initialState?.tagsToAdd || []);

  const initialTags = useMemo(() => {
    return (
      conversationTags.value?.filter((tag) => initialTagNames.has(tag.name)) ||
      []
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationTags]);

  const macro = useInput(macroBody, {
    macroName: initialState?.name || "",
    statusToSet: initialState?.statusToSet,
    snoozeDuration: initialState?.snoozeDuration,
    tagsToAdd: initialTags,
    emailSubject: initialState?.emailSubjectToSet,
    shouldAddNote: initialState?.shouldAddNote,
    noteToAddUsersMentioned: initialState?.noteToAddUsersMentioned,
    shouldForwardMessage: initialState?.shouldForwardMessage,
    messageToForwardExtraContent: initialState?.messageToForwardExtraContent,
    messageToForwardEmailAddress: initialState?.messageToForwardEmailAddress,
  });

  const [attachments, setAttachments] = useState<UploadedFile[] | undefined>(
    initialState?.attachments,
  );

  const [submitDisabledBecauseOfQuill, setSubmitDisabledBecauseOfQuill] =
    useState<boolean>(true);
  const [quill, setQuill] = useState<Quill | null>(null);

  const [noteQuill, setNoteQuill] = useState<Quill | null>(null);

  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);

  const handleSaveMacro = async () => {
    if (!quill) {
      return;
    }

    const getContentsFromQuill = (quill: Quill | undefined | null) => {
      if (!quill) {
        return { content: "", htmlContent: "" };
      }
      // This is based on quill's getText method, but with the addition of handling links
      // See https://github.com/slab/quill/blob/ebe16ca24724ac4f52505628ac2c4934f0a98b85/packages/quill/src/core/editor.ts#L212
      const textContent = quill
        .getContents()
        .filter((op) => typeof op.insert === "string")
        .map((op) => {
          let str = op.insert?.toString();
          if (op.attributes?.link) {
            str += ` (${op.attributes?.link?.toString()})`;
          }
          return str;
        })
        .join("");
      const content = textContent?.substring(0, textContent.length - 1) || ""; // Remove trailing newline added by Quill
      const htmlContent = quill.getSemanticHTML() || "";
      return { content, htmlContent };
    };
    const [
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { content, htmlContent },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { content: noteToAddContent, htmlContent: noteToAddHtmlContent },
    ] = Object.values(whichQuillLookup)
      .map((quillData) => quillData.quill)
      .map(getContentsFromQuill);

    const inputValue = macro.value;

    const bodyToPut: PutMacroBody = {
      name: inputValue.macroName,
      content,
      htmlContent,
      statusToSet: undefined,
      emailSubjectToSet: undefined,
      snoozeDuration: undefined,
      tagsToAdd: undefined,
      attachments,
    };

    const applyAutomationChangesToPutMacroBody: Record<
      MacroAutomation,
      (body: PutMacroBody) => void
    > = {
      [MacroAutomation.FORWARD_MESSAGE]: (body: PutMacroBody) => {
        body.shouldForwardMessage = inputValue.shouldForwardMessage;
        body.messageToForwardEmailAddress =
          inputValue.messageToForwardEmailAddress?.toLowerCase();
        body.messageToForwardExtraContent =
          inputValue.messageToForwardExtraContent;
      },
      [MacroAutomation.ADD_INTERNAL_NOTE]: (body: PutMacroBody) => {
        body.shouldAddNote = inputValue.shouldAddNote;
        body.noteToAddContent = noteToAddContent;
        body.noteToAddHtmlContent = noteToAddHtmlContent;
        body.noteToAddUsersMentioned = inputValue.noteToAddUsersMentioned;
      },
      [MacroAutomation.SET_STATUS]: (body: PutMacroBody) => {
        body.statusToSet = inputValue.statusToSet;
        body.snoozeDuration = inputValue.snoozeDuration;
      },
      [MacroAutomation.ADD_TAGS]: (body: PutMacroBody) => {
        body.tagsToAdd = inputValue.tagsToAdd?.map((tag) => tag.name);
      },
      [MacroAutomation.CHANGE_EMAIL_SUBJECT]: (body: PutMacroBody) => {
        body.emailSubjectToSet = inputValue.emailSubject;
      },
    };

    for (const automation of Object.values(
      applyAutomationChangesToPutMacroBody,
    )) {
      automation(bodyToPut);
    }

    await submitMacro(bodyToPut);
  };

  const handleUpload = async ({
    event,
    file,
  }: {
    event?: ChangeEvent<HTMLInputElement>;
    file?: File;
  }) => {
    const fileToUpload = file || event?.target?.files?.[0];
    if (!fileToUpload) {
      return;
    }

    const form = new FormData();
    form.append("file", fileToUpload);
    form.append("fileName", fileToUpload.name);
    const response = await uploadFile(apiClient, form);
    if (response.success) {
      amplitude.logEvent("create-attachment", { file: fileToUpload.name });
      const newAttachment = { ...response.body };
      setAttachments((oldAttachments) => {
        return [newAttachment, ...(oldAttachments || [])];
      });
    } else {
      toast(conversationFileUploadErrorMessages[response.error], {
        variant: "error",
      });
    }
  };
  const enum QuillEditorsOnThisComponent {
    /** Where the main content of the template is */
    MAIN = "main",
    /** Where the note is */
    NOTE = "note",
  }
  /**
   * With notes, there's now 2 quill editors on this page. We need to know which one is focused
   * so we can insert macro variables into the correct editor.
   */
  const whichQuillLookup = useMemo(() => {
    return {
      [QuillEditorsOnThisComponent.MAIN]: {
        quill,
        initialHtml: initialState?.htmlContent,
      },
      [QuillEditorsOnThisComponent.NOTE]: {
        quill: noteQuill,
        initialHtml: initialState?.noteToAddHtmlContent,
      },
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quill, noteQuill, initialState]);

  /**
   * Plug in the internal note automation edit mode
   * (TODO some of the quill logic could move into the automation
   * as noteToAddContent and noteToAddHtmlContent)
   */
  const internalNoteAutomationEditMode = (
    <AddInternalNoteAutomation.EditMode
      client={client}
      noteToAddUsersMentioned={macro.inputs.noteToAddUsersMentioned.value ?? []}
      quill={noteQuill}
      setNoteToAddUsersMentioned={(value: string[]) =>
        macro.inputs.noteToAddUsersMentioned.setValue(value)
      }
      setQuill={setNoteQuill}
      setShouldAddNote={(value: boolean) =>
        macro.inputs.shouldAddNote.setValue(value)
      }
      shouldAddNote={macro.inputs.shouldAddNote.value ?? false}
      team={team}
    />
  );

  const onVariableClick = (variable: MacroVariableType) => {
    const { quill: quillToInsertIn } = whichQuillLookup[whichEditorFocused];
    if (!quillToInsertIn) {
      return;
    }
    insertMacroVariableIntoQuill(quillToInsertIn, variable);
  };

  const [whichEditorFocused, setWhichEditorFocused] =
    useState<QuillEditorsOnThisComponent>(QuillEditorsOnThisComponent.MAIN);

  useEffect(() => {
    const deregisters = Object.entries(whichQuillLookup).map(
      ([name, { quill: whichQuillToSetup, initialHtml }]) => {
        if (!whichQuillToSetup) {
          return () => {};
        }
        if (initialState) {
          whichQuillToSetup.clipboard.dangerouslyPasteHTML(initialHtml || "");
          clearColoringAtCursor(
            whichQuillToSetup,
            whichQuillToSetup.getText().length,
          );

          whichQuillToSetup.blur();
        }
        const focusHandler = (event: FocusEvent) => {
          if (event.target !== whichQuillToSetup.root) {
            return;
          }
          setWhichEditorFocused(name as QuillEditorsOnThisComponent);
        };
        whichQuillToSetup.root.addEventListener("focus", focusHandler);
        return () => {
          whichQuillToSetup.root.removeEventListener("focus", focusHandler);
        };
      },
    );
    return () => {
      for (const deregister of deregisters) {
        deregister();
      }
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whichQuillLookup]);

  const title = initialState
    ? `Edit template: ${initialState.name}`
    : "Create new template";

  const statusOptions = [
    {
      id: "none",
      value: undefined,
      type: "text" as const,
      text: "No status change",
    },
    ...Object.values(MacroStatusToSet).map((status) => ({
      id: status,
      value: status,
      type: "text" as const,
      text: status.charAt(0).toUpperCase() + status.slice(1),
    })),
  ];

  const selectedStatusOption =
    statusOptions.find((opt) => opt.value === macro.value.statusToSet) ||
    statusOptions[0];

  const TemplatesForEditingMacroAutomations: Record<
    MacroAutomation,
    ReactElement
  > = {
    [MacroAutomation.SET_STATUS]: (
      <Flex dir="column" gap="xs">
        <RedoSingleSelectDropdownInput
          description={
            macro.value.snoozeDuration
              ? `Until ${snoozeDurationToLabel(macro.value.snoozeDuration).charAt(0).toLowerCase()}${snoozeDurationToLabel(macro.value.snoozeDuration).slice(1)}`
              : undefined
          }
          label="Set status"
          options={statusOptions}
          optionSelected={({ item }) => {
            const value = item.value as MacroStatusToSet | undefined;
            if (value === MacroStatusToSet.SNOOZED) {
              setSnoozeModalOpen(true);
            } else {
              macro.inputs.snoozeDuration.setValue(undefined);
            }
            macro.inputs.statusToSet.setValue(value);
          }}
          placeholder="No status change"
          selectedItem={selectedStatusOption}
          size="sm"
        />
      </Flex>
    ),
    [MacroAutomation.ADD_TAGS]: (
      <Flex dir="column" gap="xs">
        <Text fontSize="sm" fontWeight="medium" textColor="secondary">
          Add tags
        </Text>
        <ConversationTagInput
          currentTags={macro.inputs.tagsToAdd.value}
          dropdownPlacement="top"
          setCurrentTags={macro.inputs.tagsToAdd.setValue}
          showAddButtonToEnterInput
        />
      </Flex>
    ),
    [MacroAutomation.CHANGE_EMAIL_SUBJECT]: (
      <Flex dir="column" gap="md">
        <RedoCheckbox
          description="This only applies when used on email tickets"
          label="Update email subject"
          setValue={(value) => {
            macro.inputs.emailSubject.setValue(value ? "" : undefined);
          }}
          size="sm"
          value={macro.inputs.emailSubject.value !== undefined}
        />

        {macro.inputs.emailSubject.value !== undefined && (
          <RedoTextInput
            placeholder="Enter email subject"
            setValue={macro.inputs.emailSubject.setValue}
            size="sm"
            value={macro.inputs.emailSubject.value || ""}
          />
        )}
      </Flex>
    ),
    [MacroAutomation.ADD_INTERNAL_NOTE]: <>{internalNoteAutomationEditMode}</>,
    [MacroAutomation.FORWARD_MESSAGE]: (
      <ForwardMessageAutomationEditMode
        content={macro.inputs.messageToForwardExtraContent.value || ""}
        email={macro.inputs.messageToForwardEmailAddress.value || ""}
        enabled={macro.inputs.shouldForwardMessage.value || false}
        setContent={(value) =>
          macro.inputs.messageToForwardExtraContent.setValue(value)
        }
        setEmail={(value) =>
          macro.inputs.messageToForwardEmailAddress.setValue(value)
        }
        setEnabled={(value) =>
          macro.inputs.shouldForwardMessage.setValue(value)
        }
      />
    ),
  };

  return (
    <>
      <RedoModal
        hideFooter
        isOpen
        modalSize="lg"
        noPadding
        onModalCloseRequested={cancel}
        title={title}
      >
        <Flex overflow="hidden">
          <Flex borderColor="secondary" dir="column" flex="1">
            <Flex
              align="center"
              justify="space-between"
              p="md"
              textColor="tertiary"
            >
              <Text fontSize="sm" fontWeight="medium" textColor="tertiary">
                Variables
              </Text>
            </Flex>
            <Flex dir="column" overflow="auto" p="lg">
              <MacroVariableList onClick={onVariableClick} />
            </Flex>
          </Flex>
          <Flex dir="column" flex="2" justify="space-between" overflow="auto">
            <Flex dir="column" overflow="auto">
              <Flex dir="column" gap="lg" grow={1} p="lg" shrink={0}>
                <RedoTextInput
                  label="Template name"
                  placeholder="Template name"
                  setValue={macro.inputs.macroName.setValue}
                  size="sm"
                  value={macro.inputs.macroName.value}
                />
                <Flex dir="column">
                  <Flex className={quillEditorCss.container} dir="column">
                    <QuillEditor
                      defaultHtmlContents=""
                      editorClassName={quillEditorCss.editor}
                      onQuillEditorEmptyChange={setSubmitDisabledBecauseOfQuill}
                      placeholder="Start typing..."
                      readOnly={false}
                      ref={setQuill}
                      toolbarId="macro-toolbar"
                    />
                  </Flex>
                  <QuillToolbarOptions
                    additionalToolbarItems={[
                      {
                        icon: PaperclipIcon,
                        buttonTitle: "Attach file",
                        identifier: "attach-file",
                        element: (
                          <QuillToolbarUploadFile
                            clearInput={(event: any) =>
                              (event.target.value = "")
                            }
                            handleUpload={handleUpload}
                          />
                        ),
                      },
                      {
                        icon: ClearFormatIcon,
                        buttonTitle: "Clear formatting",
                        identifier: "clear-formatting",
                        element: (
                          <RedoButton
                            buttonId="clear-formatting"
                            hierarchy="tertiary"
                            IconLeading={ClearFormatIcon}
                            onClick={() => {
                              if (!quill) {
                                return;
                              }
                              const selection = quill.getSelection();
                              quill.removeFormat(
                                selection?.index || 0,
                                selection?.length || 0,
                              );
                            }}
                          />
                        ),
                      },
                    ]}
                    emailMode
                    toolbarId="macro-toolbar"
                  />
                  <QuillAttachmentCarousel
                    attachments={attachments}
                    removeFileFromDrafts={(fileUrl) => {
                      setAttachments((oldAttachments) => {
                        if (!oldAttachments) {
                          return;
                        }
                        return oldAttachments.filter(
                          (attachment) => attachment.url !== fileUrl,
                        );
                      });
                    }}
                  />
                </Flex>
              </Flex>
              <Flex dir="column" gap="lg" grow={1} p="lg" shrink={0}>
                <Flex dir="column" gap="xs">
                  <Text fontSize="md" fontWeight="semibold">
                    Automations
                  </Text>
                  <Text fontSize="sm" textColor="tertiary">
                    These settings will take place if you use this template in a
                    support ticket
                  </Text>
                </Flex>

                {Object.entries(TemplatesForEditingMacroAutomations).map(
                  ([automation, element]) => (
                    <Fragment key={automation}>{element}</Fragment>
                  ),
                )}
              </Flex>
            </Flex>
            <Flex gap="md" justify="flex-end" p="lg" w="full">
              <RedoButton
                hierarchy="secondary"
                onClick={cancel}
                text="Cancel"
              />
              <RedoButton
                disabled={
                  macro.allErrors.length > 0 || submitDisabledBecauseOfQuill
                }
                hierarchy="primary"
                onClick={handleSaveMacro}
                text="Save template"
              />
            </Flex>
          </Flex>
        </Flex>
      </RedoModal>
      <SelectSnoozeDurationModal
        onClose={() => {
          if (!macro.inputs.snoozeDuration.value) {
            macro.inputs.statusToSet.setValue(undefined);
          }
          setSnoozeModalOpen(false);
        }}
        onDurationSelect={(duration) => {
          macro.inputs.snoozeDuration.setValue(duration);
          setSnoozeModalOpen(false);
        }}
        open={snoozeModalOpen}
      />
    </>
  );
});

const MacroVariableList = memo(function MacroVariableList({
  onClick,
}: {
  onClick(variable: MacroVariableType): void;
}) {
  return (
    <Flex dir="column" gap="md">
      {MACRO_VARIABLES?.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ).map((variable) => {
        return (
          <Flex
            as="button"
            key={variable.id}
            onClick={() => onClick(variable)}
            px="md"
            style={{ cursor: "pointer" }}
          >
            <MacroVariablePill variable={variable} />
          </Flex>
        );
      })}
    </Flex>
  );
});

const snoozeDurationToLabel = (duration: SnoozeDuration): string => {
  switch (duration) {
    case SnoozeDuration.ONE_HOUR:
      return "In one hour";
    case SnoozeDuration.SAME_DAY:
      return "Later in the day";
    case SnoozeDuration.ONE_DAY:
      return "The next day";
    case SnoozeDuration.TWO_DAYS:
      return "Two days later";
    case SnoozeDuration.SATURDAY:
      return "The next Saturday";
    case SnoozeDuration.MONDAY:
      return "The next Monday";
    default: {
      const exhaustiveCheck: never = duration;
      throw new Error(`Unhandled snooze duration: ${exhaustiveCheck}`);
    }
  }
};

const formatSnoozeOptionText = (label: string, time: Date): string => {
  const formattedTime = time.toLocaleString([], {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${label} - ${formattedTime}`;
};

const SelectSnoozeDurationModal = memo(function SelectSnoozeDurationModal({
  open,
  onClose,
  onDurationSelect,
}: {
  open: boolean;
  onClose(): void;
  onDurationSelect(duration: SnoozeDuration): void;
}) {
  const today = new Date();
  today.setHours(18, 0, 0);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0);

  const twoDays = new Date();
  twoDays.setDate(twoDays.getDate() + 2);
  twoDays.setHours(8, 0, 0);

  const nextSaturday = new Date();
  nextSaturday.setDate(
    nextSaturday.getDate() + ((6 + 7 - nextSaturday.getDay()) % 7 || 7),
  );
  nextSaturday.setHours(8, 0, 0);

  const nextMonday = new Date();
  nextMonday.setDate(
    nextMonday.getDate() + ((1 + 7 - nextMonday.getDay()) % 7 || 7),
  );
  nextMonday.setHours(8, 0, 0);

  if (!open) {
    return null;
  }

  return (
    <RedoModal
      headerBorder={false}
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={onClose}
      subtitle="Choose how long tickets will be snoozed for when this template is used"
      title="Snooze ticket until"
    >
      <Flex dir="column" gap="sm" overflow="auto" px="md">
        <RedoButton
          hierarchy="secondary"
          onClick={() => onDurationSelect(SnoozeDuration.SAME_DAY)}
          text={formatSnoozeOptionText(
            snoozeDurationToLabel(SnoozeDuration.SAME_DAY),
            today,
          )}
        />
        <RedoButton
          hierarchy="secondary"
          onClick={() => onDurationSelect(SnoozeDuration.ONE_DAY)}
          text={formatSnoozeOptionText(
            snoozeDurationToLabel(SnoozeDuration.ONE_DAY),
            tomorrow,
          )}
        />
        <RedoButton
          hierarchy="secondary"
          onClick={() => onDurationSelect(SnoozeDuration.TWO_DAYS)}
          text={formatSnoozeOptionText(
            snoozeDurationToLabel(SnoozeDuration.TWO_DAYS),
            twoDays,
          )}
        />
        <RedoButton
          hierarchy="secondary"
          onClick={() => onDurationSelect(SnoozeDuration.SATURDAY)}
          text={formatSnoozeOptionText(
            snoozeDurationToLabel(SnoozeDuration.SATURDAY),
            nextSaturday,
          )}
        />
        <RedoButton
          hierarchy="secondary"
          onClick={() => onDurationSelect(SnoozeDuration.MONDAY)}
          text={formatSnoozeOptionText(
            snoozeDurationToLabel(SnoozeDuration.MONDAY),
            nextMonday,
          )}
        />
      </Flex>
    </RedoModal>
  );
});
